import { useEffect, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';

import { optimisticMutation } from './optimistic-mutation';
import { ConnectorTokenStatus, type ConnectorRecord, type CredentialPlacement } from '../sections/types';
import {
  CONNECT_CHANNEL,
  CONNECT_RESULT_TYPE,
  type ConnectionResult,
  type ConnectResultMessage,
} from './connector-oauth';

export const CONNECTORS_QUERY_KEY = ['connectors'] as const;
export const CONNECTION_STATUS_QUERY_KEY = ['connection-status'] as const;

interface ConnectorsListResponse {
  connectors: ConnectorRecord[];
  user_enabled_connector_ids: string[];
  /** Rows the user turned off. They stay linked, so they also appear above. */
  user_disabled_connector_ids?: string[];
  user_connector_statuses?: Record<
    string,
    {
      token_status: ConnectorTokenStatus;
      token_status_at: string | null;
      token_status_error: string | null;
    }
  >;
}

interface ConnectorConnectionStatus {
  connector_id: string;
  token_status: ConnectorTokenStatus;
  token_status_at: string | null;
  token_status_error: string | null;
}

export enum McpAuthType {
  None = 'none',
  OAuth2 = 'oauth2',
  ApiKey = 'apikey',
}

// The Ari runtime opens every MCP server with Streamable HTTP.
export type McpTransport = 'streamable_http';

/** What a probed MCP server asks for, and what the user must supply for it. */
export enum McpProbeOutcome {
  None = 'none',
  OAuth = 'oauth',
  OAuthManualClient = 'oauth_manual_client',
  ApiKey = 'apikey',
}

/** Whether a connector's auth type came from the probe or from the user. */
export enum McpAuthSource {
  Detected = 'detected',
  UserForced = 'user_forced',
}

export interface ProbeMcpResult {
  outcome: McpProbeOutcome;
  mcp_url: string;
  transport: McpTransport;
  server_name?: string;
  /** False when the server answered but its tool list could not be read. */
  tools_listed: boolean;
}

export interface DiscoverMcpParams {
  url: string;
  name?: string;
}

export interface DiscoverMcpResult {
  mcp_url?: string;
  transport: McpTransport;
  auth_type: McpAuthType;
  auth0_connection?: string | null;
  connector_id?: string;
  server_name?: string;
  needs_auth: boolean;
}

export type AddCredentialParams = {
  name?: string;
  placement: CredentialPlacement;
  authSource?: McpAuthSource;
} & ({ kind: 'host'; host: string } | { kind: 'mcp'; url: string });

export interface AddCredentialResult {
  connector_id: string;
  credential_ref: string;
  host: string;
  auth0_connection: string;
  needs_auth: true;
}

export interface RefreshConnectorStatusResponse {
  statuses: Array<{
    connector_id: string;
    token_status: ConnectorTokenStatus;
    token_status_at: string | null;
    token_status_error: string | null;
  }>;
}

export interface UseConnectorsApi {
  fetchConnectors: () => Promise<ConnectorsListResponse>;
  enableUserConnector: (connectorId: string) => Promise<unknown>;
  disableUserConnector: (connectorId: string) => Promise<unknown>;
  /** Turn a linked connector off or on. Keeps the row, the connection and any stored key. */
  setUserConnectorEnabled: (connectorId: string, enabled: boolean) => Promise<unknown>;
  deleteConnector: (connectorId: string) => Promise<unknown>;
  discoverMcp: (params: DiscoverMcpParams) => Promise<DiscoverMcpResult>;
  /** Ask a server what auth it needs. Stores nothing: no connector, no Auth0 connection. */
  probeMcp: (params: { url: string }) => Promise<ProbeMcpResult>;
  /** Bind an endpoint to the API-key broker. Never carries a key value. */
  addCredential: (params: AddCredentialParams) => Promise<AddCredentialResult>;
  getConnectionStatus: () => Promise<ConnectorConnectionStatus[]>;
  /** Initiate the OAuth popup flow for a connector. Both apps already have an
   *  authenticated wrapper; this is just the wire-level call. */
  initiateConnection: (connectorId: string) => Promise<ConnectionResult>;
  /** Disconnect a connector (server-side revoke + DB cleanup). */
  disconnectProvider: (connectorId: string) => Promise<boolean>;
  /** Refresh the vault session in the OAuth popup before a removal retries (CLOUD-1040). */
  reauthorizeConnector: (connectorId: string) => Promise<ConnectionResult>;
  /** Optional. Apps that don't expose refresh-status (apps/ari) can omit this. */
  refreshConnectorStatus?: () => Promise<RefreshConnectorStatusResponse>;
}

export type UseConnectorsReturn = ReturnType<typeof useConnectors>;

export function useConnectors(api: UseConnectorsApi) {
  const queryClient = useQueryClient();

  const {
    data: connectorsData,
    isPending: isPendingCatalog,
    refetch: refetchConnectorsQuery,
  } = useQuery({
    queryKey: CONNECTORS_QUERY_KEY,
    queryFn: api.fetchConnectors,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });

  const { data: connectorStatuses, isPending: isPendingConnections } = useQuery({
    queryKey: CONNECTION_STATUS_QUERY_KEY,
    queryFn: api.getConnectionStatus,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });

  const markConnectorValid = (connectorId: string) => {
    queryClient.setQueryData<ConnectorsListResponse | undefined>(CONNECTORS_QUERY_KEY, (old) => {
      if (!old) return old;
      const enabledIds = new Set(old.user_enabled_connector_ids);
      enabledIds.add(connectorId);
      return {
        ...old,
        user_enabled_connector_ids: [...enabledIds],
        user_connector_statuses: {
          ...(old.user_connector_statuses ?? {}),
          [connectorId]: {
            token_status: ConnectorTokenStatus.Valid,
            token_status_at: null,
            token_status_error: null,
          },
        },
      };
    });
    queryClient.setQueryData<ConnectorConnectionStatus[]>(CONNECTION_STATUS_QUERY_KEY, (old) => {
      const withoutConnector = (old ?? []).filter((status) => status.connector_id !== connectorId);
      return [
        ...withoutConnector,
        {
          connector_id: connectorId,
          token_status: ConnectorTokenStatus.Valid,
          token_status_at: null,
          token_status_error: null,
        },
      ];
    });
  };

  // A connect can finish after its opener reported a cancel, so refetch on any success.
  useEffect(() => {
    const channel = new BroadcastChannel(CONNECT_CHANNEL);
    channel.onmessage = (event: MessageEvent<Partial<ConnectResultMessage>>) => {
      if (event.data?.type !== CONNECT_RESULT_TYPE || !event.data.connected) return;
      const connectedConnectorId = event.data.connected;
      markConnectorValid(connectedConnectorId);
      void Promise.all([
        queryClient.invalidateQueries({ queryKey: CONNECTORS_QUERY_KEY }, { cancelRefetch: false }),
        queryClient.invalidateQueries({ queryKey: CONNECTION_STATUS_QUERY_KEY }, { cancelRefetch: false }),
      ]).finally(() => markConnectorValid(connectedConnectorId));
    };
    return () => channel.close();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryClient]);

  // Both queries must resolve before we compute connector state. Token-backed
  // connectors need the connector-status signal in addition to the catalog.
  const isLoading = isPendingCatalog || isPendingConnections;

  const connectorsCatalog = connectorsData?.connectors ?? [];

  const userEnabledConnectorIds = useMemo(
    () => new Set(connectorsData?.user_enabled_connector_ids ?? []),
    [connectorsData?.user_enabled_connector_ids]
  );

  const userDisabledConnectorIds = useMemo(
    () => new Set(connectorsData?.user_disabled_connector_ids ?? []),
    [connectorsData?.user_disabled_connector_ids]
  );

  const userConnectorStatuses = useMemo(() => {
    const map = new Map<string, ConnectorTokenStatus>();
    const statuses = connectorsData?.user_connector_statuses ?? {};
    for (const [cid, value] of Object.entries(statuses)) {
      map.set(cid, value.token_status);
    }
    for (const value of connectorStatuses ?? []) {
      map.set(value.connector_id, value.token_status);
    }
    return map;
  }, [connectorStatuses, connectorsData?.user_connector_statuses]);

  const connectedConnectorIds = useMemo(
    () =>
      new Set(
        [...userConnectorStatuses.entries()]
          .filter(([, status]) => status === ConnectorTokenStatus.Valid)
          .map(([connectorId]) => connectorId)
      ),
    [userConnectorStatuses]
  );

  const enableMutation = useMutation(
    optimisticMutation<unknown, string, ConnectorsListResponse>({
      queryClient,
      queryKey: CONNECTORS_QUERY_KEY,
      mutationFn: api.enableUserConnector,
      updater: (old, connectorId) => {
        if (!old) return old;
        const ids = new Set(old.user_enabled_connector_ids);
        ids.add(connectorId);
        return { ...old, user_enabled_connector_ids: [...ids] };
      },
    })
  );

  const disableMutation = useMutation(
    optimisticMutation<unknown, string, ConnectorsListResponse>({
      queryClient,
      queryKey: CONNECTORS_QUERY_KEY,
      mutationFn: api.disableUserConnector,
      updater: (old, connectorId) => {
        if (!old) return old;
        return {
          ...old,
          user_enabled_connector_ids: old.user_enabled_connector_ids.filter((id) => id !== connectorId),
        };
      },
    })
  );

  const setEnabledMutation = useMutation({
    ...optimisticMutation<unknown, { connectorId: string; enabled: boolean }, ConnectorsListResponse>({
      queryClient,
      queryKey: CONNECTORS_QUERY_KEY,
      mutationFn: ({ connectorId, enabled }) => api.setUserConnectorEnabled(connectorId, enabled),
      updater: (old, { connectorId, enabled }) => {
        if (!old) return old;
        const ids = new Set(old.user_disabled_connector_ids ?? []);
        if (enabled) ids.delete(connectorId);
        else ids.add(connectorId);
        return { ...old, user_disabled_connector_ids: [...ids] };
      },
    }),
    // The rollback restores a guess. A PATCH can fail after the row changed.
    onSettled: (_data, error) => {
      if (error) void queryClient.invalidateQueries({ queryKey: CONNECTORS_QUERY_KEY });
    },
  });

  const deleteMutation = useMutation(
    optimisticMutation<unknown, string, ConnectorsListResponse>({
      queryClient,
      queryKey: CONNECTORS_QUERY_KEY,
      mutationFn: api.deleteConnector,
      updater: (old, connectorId) => {
        if (!old) return old;
        return {
          ...old,
          connectors: old.connectors.filter((c) => c.id !== connectorId),
          user_enabled_connector_ids: old.user_enabled_connector_ids.filter((id) => id !== connectorId),
        };
      },
    })
  );

  const discoverMutation = useMutation({
    mutationFn: api.discoverMcp,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: CONNECTORS_QUERY_KEY });
    },
  });

  const addCredentialMutation = useMutation({
    mutationFn: api.addCredential,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: CONNECTORS_QUERY_KEY });
    },
  });

  const initiateConnectionMutation = useMutation({
    mutationFn: (connectorId: string) => api.initiateConnection(connectorId),
    onSuccess: (result) => {
      // Successful OAuth — the user has just (re)authorized. Both caches need
      // refreshing: /connectors for catalog/status fields, and /auth/connectors
      // for connector-status rows.
      if (result.connected) {
        const connectedConnectorId = result.connected;
        markConnectorValid(connectedConnectorId);
        void Promise.all([
          queryClient.invalidateQueries({ queryKey: CONNECTORS_QUERY_KEY }),
          queryClient.invalidateQueries({ queryKey: CONNECTION_STATUS_QUERY_KEY }),
        ]).finally(() => markConnectorValid(connectedConnectorId));
      }
    },
  });

  const disconnectMutation = useMutation({
    mutationFn: (connectorId: string) => api.disconnectProvider(connectorId),
    onMutate: async (connectorId: string) => {
      // Optimistically remove the connector from the user's enabled set and
      // connector-status cache. Both states roll back together in onError.
      await queryClient.cancelQueries({ queryKey: CONNECTORS_QUERY_KEY });
      await queryClient.cancelQueries({ queryKey: CONNECTION_STATUS_QUERY_KEY });
      const previousConnectors = queryClient.getQueryData<ConnectorsListResponse>(CONNECTORS_QUERY_KEY);
      const previousConnections = queryClient.getQueryData<ConnectorConnectionStatus[]>(CONNECTION_STATUS_QUERY_KEY);

      queryClient.setQueryData(CONNECTORS_QUERY_KEY, (old: ConnectorsListResponse | undefined) => {
        if (!old) return old;
        return {
          ...old,
          user_enabled_connector_ids: old.user_enabled_connector_ids.filter((id) => id !== connectorId),
        };
      });

      queryClient.setQueryData<ConnectorConnectionStatus[]>(CONNECTION_STATUS_QUERY_KEY, (old) =>
        (old ?? []).filter((s) => s.connector_id !== connectorId)
      );

      return { previousConnectors, previousConnections };
    },
    onError: (_err, _connectorId, context) => {
      if (context?.previousConnectors) {
        queryClient.setQueryData(CONNECTORS_QUERY_KEY, context.previousConnectors);
      }
      if (context?.previousConnections) {
        queryClient.setQueryData(CONNECTION_STATUS_QUERY_KEY, context.previousConnections);
      }
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: CONNECTORS_QUERY_KEY });
      void queryClient.invalidateQueries({
        queryKey: CONNECTION_STATUS_QUERY_KEY,
      });
    },
  });

  const reauthorizeMutation = useMutation({
    mutationFn: (connectorId: string) => api.reauthorizeConnector(connectorId),
  });

  const refreshConnectorStatusApi = api.refreshConnectorStatus;
  const refreshStatusMutation = useMutation({
    mutationFn: async () => {
      if (!refreshConnectorStatusApi) {
        throw new Error('refreshConnectorStatus is not available in this app');
      }
      return refreshConnectorStatusApi();
    },
    onSuccess: (data) => {
      queryClient.setQueryData<ConnectorsListResponse | undefined>(CONNECTORS_QUERY_KEY, (old) => {
        if (!old) return old;
        const merged = { ...(old.user_connector_statuses ?? {}) };
        for (const s of data.statuses) {
          merged[s.connector_id] = {
            token_status: s.token_status,
            token_status_at: s.token_status_at,
            token_status_error: s.token_status_error,
          };
        }
        return { ...old, user_connector_statuses: merged };
      });
      // Also refresh the connector-status cache.
      void queryClient.invalidateQueries({
        queryKey: CONNECTION_STATUS_QUERY_KEY,
      });
    },
  });

  return {
    connectorsCatalog,
    userEnabledConnectorIds,
    userDisabledConnectorIds,
    userConnectorStatuses,
    connectedConnectorIds,
    isLoading,
    refetchConnectors: async () => (await refetchConnectorsQuery({ throwOnError: true })).data,
    enableConnector: (connectorId: string) => enableMutation.mutateAsync(connectorId),
    disableConnector: (connectorId: string) => disableMutation.mutate(connectorId),
    setConnectorEnabled: (connectorId: string, enabled: boolean) =>
      setEnabledMutation.mutateAsync({ connectorId, enabled }),
    /**
     * Exposed so views can drive a confirm-then-error-recovery dialog
     * off mutation state (isError, error, variables, reset) without
     * mirroring those fields into local useState.
     */
    deleteConnectorMutation: deleteMutation,
    discoverMcp: discoverMutation.mutateAsync,
    probeMcp: api.probeMcp,
    addCredential: addCredentialMutation.mutateAsync,
    initiateConnection: (connectorId: string) => initiateConnectionMutation.mutateAsync(connectorId),
    isConnecting: initiateConnectionMutation.isPending,
    disconnectProvider: disconnectMutation.mutateAsync,
    isDisconnecting: disconnectMutation.isPending,
    /**
     * Exposed for the same reason as deleteConnectorMutation — views can
     * surface in-flight + error state from the mutation directly.
     */
    disconnectProviderMutation: disconnectMutation,
    reauthorizeConnector: reauthorizeMutation.mutateAsync,
    isReauthorizing: reauthorizeMutation.isPending,
    // Apps that don't expose /auth/connectors/refresh (e.g. apps/ari)
    // see `undefined` here, matching the optional `api.refreshConnectorStatus`
    // contract. Callers should null-check before invoking.
    refreshConnectorStatus: refreshConnectorStatusApi ? refreshStatusMutation.mutateAsync : undefined,
    isRefreshingStatus: refreshConnectorStatusApi ? refreshStatusMutation.isPending : false,
    setConnectedConnectorIds: (connectorIds: Set<string>) => {
      queryClient.setQueryData(
        CONNECTION_STATUS_QUERY_KEY,
        [...connectorIds].map((connectorId) => ({
          connector_id: connectorId,
          token_status: ConnectorTokenStatus.Valid,
          token_status_at: null,
          token_status_error: null,
        }))
      );
    },
  };
}
